import { createContext, useCallback, useContext, useState } from 'react';
import {
  DropdownsParams,
  DropdownsContextData,
  DropdownsData,
  DropdownsProviderProps,
} from './types';
import { initialState } from './constants';
import { useAdditiveDropdown } from '@services/dropdowns/additive';
import { useBuildersDropdown } from '@services/dropdowns/builders';
import { useBuilderSuperintendentsDropdown } from '@services/dropdowns/builderSuperintendents';
import { useCompaniesDropdown } from '@services/dropdowns/companies';
import { useConcreteMixDropdown } from '@services/dropdowns/concreteMix';
import { useUsersDropdown } from '@services/dropdowns/users';
import { useInventoryItemsDropdown } from '@services/dropdowns/inventoryItems';
import { useLocationsDropdown } from '@services/dropdowns/locations';
import { usePourElementsDropdown } from '@services/dropdowns/pourElements';
import { usePourMethodsDropdown } from '@services/dropdowns/pourMethods';
import { useRoutesDropdown } from '@services/dropdowns/routes';
import { useSitesDropdown } from '@services/dropdowns/sites';
import { useStagesDropdown } from '@services/dropdowns/stages';
import { useSubtasksForFilesDropdown } from '@services/dropdowns/subtasksForFiles';
import { useTasksDropdown } from '@services/dropdowns/tasks';
import { usePanelZonesDropdown } from '@services/dropdowns/panel/zones';
import { usePanelRolesDropdown } from '@services/dropdowns/panel/roles';
import { usePanelStatusesDropdown } from '@services/dropdowns/panel/statuses';
import { useWorkflowsDropdown } from '@services/dropdowns/workflows';
import { useTaskTypesDropdown } from '@services/dropdowns/taskTypes';
import { useSpecialtiesDropdown } from '@services/dropdowns/specialties';
import { useDivisionsDropdown } from '@services/dropdowns/divisions';
import { usePanelSpecialtiesDropdown } from '@services/dropdowns/panel/specialties';
import { usePanelTenantsDropdown } from '@services/dropdowns/panel/tenants';
import { usePanelUsersDropdown } from '@services/dropdowns/panel/users';
import { useTenantRolesDropdown } from '@services/dropdowns/tenantRoles';
import { useEquipmentDropdown } from '@services/dropdowns/equipment';
import { usePanelEquipmentDropdown } from '@services/dropdowns/panel/equipment';
import { usePanelMaterialsTagsDropdown } from '@services/dropdowns/panel/materialsTags';
const DropdownsContext = createContext<DropdownsContextData | Record<string, never>>({});

export const DropdownsProvider = ({ children }: DropdownsProviderProps) => {
  const [state, setState] = useState<DropdownsData>(initialState);
  const { params } = state;

  const additive = useAdditiveDropdown(params.products);
  const builders = useBuildersDropdown();
  const builderSuperintendents = useBuilderSuperintendentsDropdown(params.builderSuperintendents);
  const companies = useCompaniesDropdown(params.companies);
  const concreteMix = useConcreteMixDropdown(params.products);
  const coordinators = useUsersDropdown({
    role: ['administrator', 'project_coordinator'],
    primaryRole: ['office'],
  });
  const divisions = useDivisionsDropdown(params.divisions);
  const inventoryItems = useInventoryItemsDropdown(params.inventoryItems);
  const locations = useLocationsDropdown();
  const panel = {
    roles: usePanelRolesDropdown(),
    specialties: usePanelSpecialtiesDropdown(),
    statuses: usePanelStatusesDropdown(),
    tenants: usePanelTenantsDropdown(),
    zones: usePanelZonesDropdown(),
    crewMembers: usePanelUsersDropdown({ primaryRole: ['crew_member'] }),
    fieldUsers: usePanelUsersDropdown({ primaryRole: ['field_user'] }),
    assetMembers: usePanelEquipmentDropdown(),
    materialsTags: usePanelMaterialsTagsDropdown(),
  };
  const pourElements = usePourElementsDropdown();
  const pourMethods = usePourMethodsDropdown();
  const routes = useRoutesDropdown();
  const sites = useSitesDropdown();
  const specialties = useSpecialtiesDropdown();
  const stages = useStagesDropdown();
  const subtasksForFiles = useSubtasksForFilesDropdown(params.subtasksForFiles);
  const superintendents = useUsersDropdown({ role: ['superintendent'], primaryRole: ['office'] });
  const tasks = useTasksDropdown(params.tasks);
  const taskTypes = useTaskTypesDropdown();
  const workflows = useWorkflowsDropdown(params.workflows);
  const tenantRoles = useTenantRolesDropdown({ primaryRole: params.primaryRole });
  const crewMembers = useUsersDropdown({
    primaryRole: ['crew_member'],
    active: true,
  });
  const fieldUsers = useUsersDropdown({
    primaryRole: ['field_user'],
    active: true,
    ...params.users,
  });
  const assetMembers = useEquipmentDropdown();

  const setParams = useCallback((value: Partial<DropdownsParams>) => {
    setState((prev) => ({ ...prev, params: { ...prev.params, ...value } }));
  }, []);

  const setDisabled = (condition = false) => condition;

  return (
    <DropdownsContext.Provider
      value={{
        ...state,
        setParams,
        additive: {
          options: additive.data?.records || [],
          disabled: setDisabled(additive.isLoading || !additive.data?.records?.length),
        },
        boolean: {
          options: ['yes', 'no'] as const,
          disabled: setDisabled(),
        },
        builders: {
          options: builders.data?.records || [],
          disabled: setDisabled(builders.isLoading || !builders.data?.records?.length),
        },
        builderSuperintendents: {
          options: builderSuperintendents.data?.records || [],
          disabled: setDisabled(
            builderSuperintendents.isLoading || !builderSuperintendents.data?.records?.length,
          ),
        },
        companies: {
          options: {
            companies: companies.data?.companies || [],
            workflowContacts: companies.data?.workflowContacts || [],
            projectContacts: companies.data?.projectContacts || [],
          },
          disabled: setDisabled(
            companies.isLoading ||
              (!companies.data?.companies?.length &&
                !companies.data?.workflowContacts?.length &&
                !companies.data?.projectContacts?.length),
          ),
        },
        concreteMix: {
          options: concreteMix.data?.records || [],
          disabled: setDisabled(concreteMix.isLoading || !concreteMix.data?.records?.length),
        },
        divisions: {
          options: divisions.data?.records || [],
          disabled: setDisabled(divisions.isLoading || !divisions.data?.records?.length),
        },
        coordinators: {
          options: coordinators.data?.records || [],
          disabled: setDisabled(coordinators.isLoading || !coordinators.data?.records?.length),
        },
        inventoryItems: {
          options: inventoryItems.data?.records || [],
          disabled: setDisabled(inventoryItems.isLoading || !inventoryItems.data?.records?.length),
        },
        locations: {
          options: locations.data?.records || [],
          disabled: setDisabled(locations.isLoading || !locations.data?.records?.length),
        },
        panel: {
          roles: {
            options: panel.roles.data?.records || [],
            disabled: setDisabled(panel.roles.isLoading || !panel.roles.data?.records?.length),
          },
          specialties: {
            options: panel.specialties.data?.records || [],
            disabled: setDisabled(
              panel.specialties.isLoading || !panel.specialties.data?.records?.length,
            ),
          },
          statuses: {
            options: panel.statuses.data?.records || [],
            disabled: setDisabled(
              panel.statuses.isLoading || !panel.statuses.data?.records?.length,
            ),
          },
          tenants: {
            options: panel.tenants.data?.records || [],
            disabled: setDisabled(panel.tenants.isLoading || !panel.tenants.data?.records?.length),
          },
          zones: {
            options: panel.zones.data?.records || [],
            disabled: setDisabled(panel.zones.isLoading || !panel.zones.data?.records?.length),
          },
          crewMembers: {
            options: panel.crewMembers.data?.records || [],
            disabled: setDisabled(
              panel.crewMembers.isLoading || !panel.crewMembers.data?.records?.length,
            ),
          },
          fieldUsers: {
            options: panel.fieldUsers.data?.records || [],
            disabled: setDisabled(
              panel.fieldUsers.isLoading || !panel.fieldUsers.data?.records?.length,
            ),
          },
          assetMembers: {
            options: panel.assetMembers.data?.records || [],
            disabled: setDisabled(
              panel.assetMembers.isLoading || !panel.assetMembers.data?.records?.length,
            ),
          },
          materialsTags: {
            options: panel.materialsTags.data?.records || [],
            disabled: setDisabled(
              panel.materialsTags.isLoading || !panel.materialsTags.data?.records?.length,
            ),
          },
        },
        pourElements: {
          options: pourElements.data?.records || [],
          disabled: setDisabled(pourElements.isLoading || !pourElements.data?.records?.length),
        },
        pourMethods: {
          options: pourMethods.data?.records || [],
          disabled: setDisabled(pourMethods.isLoading || !pourMethods.data?.records?.length),
        },
        rateTypes: {
          options: ['SPACING_IN_MINUTES', 'QTY_PER_HOUR'] as const,
          disabled: setDisabled(),
        },
        routes: {
          options: routes.data?.records || [],
          disabled: setDisabled(routes.isLoading || !routes.data?.records?.length),
        },
        sites: {
          options: sites.data?.records || [],
          disabled: setDisabled(sites.isLoading || !sites.data?.records?.length),
        },
        specialties: {
          options: specialties.data?.records || [],
          disabled: setDisabled(specialties.isLoading || !specialties.data?.records?.length),
        },
        stages: {
          options: stages.data?.records || [],
          disabled: setDisabled(stages.isLoading || !stages.data?.records?.length),
        },
        states: {
          options: ['follow_up', 'pre_construction', 'construction', 'billing'] as const,
          disabled: setDisabled(),
        },
        statuses: {
          options: ['draft', 'pending', 'approved', 'rejected'] as const,
          disabled: setDisabled(),
        },
        subtasksForFiles: {
          options: subtasksForFiles.data?.records || [],
          disabled: setDisabled(subtasksForFiles.isLoading),
        },
        superintendents: {
          options: superintendents.data?.records || [],
          disabled: setDisabled(
            superintendents.isLoading || !superintendents.data?.records?.length,
          ),
        },
        tasks: {
          options: tasks.data?.records || [],
          disabled: setDisabled(tasks.isLoading || !tasks.data?.records?.length),
        },
        taskTypes: {
          options: taskTypes.data?.records || [],
          disabled: setDisabled(taskTypes.isLoading || !taskTypes.data?.records?.length),
        },
        usersStatuses: {
          options: ['active', 'deactivated'] as const,
          disabled: setDisabled(),
        },
        workflows: {
          options: workflows.data?.records || [],
          disabled: setDisabled(workflows.isLoading || !workflows.data?.records?.length),
        },
        workloadTypes: {
          options: ['month', 'two-week', 'week', 'day'] as const,
          disabled: setDisabled(),
        },
        timeMode: {
          options: ['custom', 'fullDay', 'default'] as const,
          disabled: setDisabled(),
        },
        crewMembers: {
          options: crewMembers.data?.records?.filter((el) => el.id !== 'unassigned') || [],
          disabled: setDisabled(crewMembers.isLoading),
        },
        fieldUsers: {
          options: fieldUsers.data?.records?.filter((el) => el.id !== 'unassigned') || [],
          disabled: setDisabled(fieldUsers.isLoading),
        },
        tenantRoles: {
          options: tenantRoles.data?.records || [],
          disabled: setDisabled(tenantRoles.isLoading || !tenantRoles.data?.records?.length),
        },
        assetMembers: {
          options: assetMembers.data?.records || [],
          disabled: setDisabled(assetMembers.isLoading),
        },
      }}>
      {children}
    </DropdownsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useDropdownsContext = () => useContext(DropdownsContext);
